import axios from 'axios';
import { LEADS_TYPES } from '@/views/RenovaDashboard/LeadsParts/constants';
import {cloneDeep, isEqual} from 'lodash';
import {stepTypes} from '@/views/RenovaDashboard/OnboardingParts/onboarding-steps';
import { isBuiltInLeadInsuranceType } from '@/views/RenovaDashboard/ProgramsParts/components/constants';

export const defaultLeadsFilters = {
  clientType: [],
  insuranceCompanyIds: [],
  paymentType: [],
  paymentStatus: [],
  transactionStatus: [],
  searchQuery: '',
};
export const defaultRequestFilters = {
  agentCommissionPaymentStatuses: [],
  insuranceTypes: [],
  leadCreatedFrom: '',
  leadCreatedTo: '',
  withoutAgentCommissionPaymentStatus: false,
  paymentDateTo: '',
  paymentDateFrom: '',
  tasks: [],
  activity: [],
  currencyCodes: [],
  agentUserIds: [],
};
// Locked "dashboard context" bucket. Set ONLY by the sales-breakdown deep-link
// (SalesBreakdownCards.vue), never by the leads filter UI, so the leads list can reproduce the
// exact WIN-in-period set the dashboard counted. `reportingDate*` are the dashboard's from/to and
// filter on the backend's reporting date (COALESCE(payment_date, stage_updated_at, updated)).
export const defaultContextFilters = {
  stages: [],
  reportingDateFrom: '',
  reportingDateTo: '',
  // REN-1182 — set only by the Master Upload wizard's "Open in Leads", so the
  // list shows exactly the leads one import created. Filters on
  // insurance_lead.lead_import_history_id server-side.
  importIds: [],
};
const isNarrowed = (current, defaults) => Object.keys(defaults)
  .some((key) => !isEqual((current || {})[key], defaults[key]));
// Whether the last /lead/list call was narrowed server-side, by the filter UI or by a dashboard
// deep-link. Client-side `filters` are not considered: they only trim the already-fetched list,
// the response itself stays complete.
export const hasActiveRequestFilters = (requestFilters = {}, contextFilters = {}) => (
  isNarrowed(requestFilters, defaultRequestFilters)
  || isNarrowed(contextFilters, defaultContextFilters)
);
export const filterLeads = (leads, filters) => leads.filter((lead) => {
  const searchQueryLower = filters.searchQuery.toLowerCase();
  const searchWords = searchQueryLower.split(' ').filter((word) => word.length > 0);

  return (
    (filters?.clientType?.length === 0 || filters.clientType?.includes(lead.clientType))
      && (filters.insuranceCompanyIds?.length === 0
        || (!lead.insuranceCompany && filters.insuranceCompanyIds?.includes('NOT_ASSIGNED'))
        || (lead.insuranceCompany
          && filters.insuranceCompanyIds?.includes(lead.insuranceCompany?.id)))
      && (filters.paymentType?.length === 0 || filters.paymentType?.includes(lead.paymentType))
      && (filters.paymentStatus?.length === 0 || filters?.paymentStatus?.includes(lead.paymentStatus))
      && (filters.transactionStatus?.length === 0
        || filters.transactionStatus?.includes(lead.transactionStatus))
      && (filters.searchQuery === ''
        || lead?.title?.toLowerCase().includes(searchQueryLower)
        || lead?.email?.toLowerCase().includes(searchQueryLower)
        || lead?.phone?.toLowerCase().includes(searchQueryLower)
        || (searchWords.every((word) => lead?.firstName?.toLowerCase().includes(word)
          || lead?.lastName?.toLowerCase().includes(word))))
  );
});
const state = {
  leads: {
    items: [],
    // Account-wide lead count, only ever set from an unfiltered response.
    totalCount: null,
    // How many leads the current server-side filters match.
    listCount: null,
    loading: false,
  },
  // Set when the account-wide lead count request failed, so "unknown" is never read as 0.
  leadTotalCountFailed: false,
  leadsTriggers: [],
  leadDetails: {
    data: null,
    loading: false,
    actions: {
      items: [],
      totalCount: 0,
      loading: false,
    },
  },
  filteredLeadsList: [],
  filters: { ...defaultLeadsFilters },
  requestFilters: { ...defaultRequestFilters },
  contextFilters: { ...defaultContextFilters },
  leadsGroups: [],
  // Global lead custom-field schema (REN-1073). Shared across every lead; values live per-lead.
  customFields: [],
  // DB-backed editable acquisition channels (REN-1073), replacing the legacy fixed enum.
  acquisitionChannels: [],
  // The provider's custom insurance types (Sales hub), deleted ones included; see the getters.
  customInsuranceTypes: [],
};

const getters = {
  leadList: () => state.leads.items,
  leadTotalCount: () => state.leads.totalCount,
  leadTotalCountFailed: () => state.leadTotalCountFailed,
  leadsListCount: () => state.leads.listCount,
  filteredLeadsList: () => state.filteredLeadsList,
  leadsLoading: () => state.leads.loading,
  leadsFilters: () => state.filters,
  leadsRequestFilters: () => state.requestFilters,
  leadsContextFilters: () => state.contextFilters,
  leadDetails: () => state.leadDetails.data,
  leadDetailsActions: () => state.leadDetails.actions,
  leadDetailsLoading: () => state.leadDetails.loading,
  leadsGroups: () => state.leadsGroups,
  customLeadsGroups: () => state.leadsGroups?.filter((group) => !!group.customStageId),
  leadsTriggers: () => state.leadsTriggers,
  leadCustomFields: () => state.customFields,
  acquisitionChannels: () => state.acquisitionChannels,
  // Live types are the ones a lead can be put on; deleted ones stay on their leads, so the leads
  // filter and labels use the full list.
  customInsuranceTypes: () => state.customInsuranceTypes.filter((type) => !type.deleted),
  allCustomInsuranceTypes: () => state.customInsuranceTypes,
};

// Distinct agents actually assigned to leads (includes agents without leads
// permission and deleted/disabled agents) — used to populate the Agent filter.
export const fetchLeadAgents = () => axios.post('/insurance/lead/agent/list', {});

export function getLeads(requestFilters, filters = {}, contextFilters = {}) {
  // The "Not assigned" option rides as a 'NOT_ASSIGNED' sentinel in the typed id/type arrays; the
  // backend can't accept it there, so translate it into the dedicated boolean flags and strip it out.
  const companyIds = (filters.insuranceCompanyIds || []).filter((v) => v !== 'NOT_ASSIGNED');
  const insuranceCompanyNotAssigned = (filters.insuranceCompanyIds || []).includes('NOT_ASSIGNED');
  // The product filter mixes built-in type keys with custom insurance type ids; the backend takes them apart.
  const selectedTypes = (requestFilters.insuranceTypes || []).filter((v) => v !== 'NOT_ASSIGNED');
  const insuranceTypes = selectedTypes.filter(isBuiltInLeadInsuranceType);
  const customInsuranceTypeIds = selectedTypes.filter((v) => !isBuiltInLeadInsuranceType(v));
  const insuranceTypeNotAssigned = (requestFilters.insuranceTypes || []).includes('NOT_ASSIGNED');

  // Locked "dashboard context" (deep-link only): forward just the fields that are actually set so
  // ordinary list loads send nothing extra and behave exactly as before.
  const context = {};
  if (contextFilters.stages?.length) context.stages = contextFilters.stages;
  if (contextFilters.reportingDateFrom) context.reportingDateFrom = contextFilters.reportingDateFrom;
  if (contextFilters.reportingDateTo) context.reportingDateTo = contextFilters.reportingDateTo;
  if (contextFilters.importIds?.length) context.importIds = contextFilters.importIds;

  return axios.post('/insurance/lead/list', {
    page: 0,
    limit: 1000,
    ...filters,
    insuranceCompanyIds: companyIds,
    insuranceCompanyNotAssigned,
    ...{
      ...requestFilters,
      insuranceTypes,
      customInsuranceTypeIds,
      insuranceTypeNotAssigned,
      agentCommissionPaymentStatuses: requestFilters.agentCommissionPaymentStatuses?.filter(
        (val) => val !== 'MISSING'
      ),
      withoutAgentCommissionPaymentStatus:
        requestFilters.agentCommissionPaymentStatuses?.includes('MISSING'),
    },
    ...context,
  });
}
export function fetchLeadTrigger(triggerId) {
  return axios
    .post('/insurance/lead/trigger/detail', { triggerId });
}
export function fetchTriggerHistory(triggerId) {
  return axios
    .post('/insurance/lead/trigger/history/list', { triggerId });
}
export function retryTriggerHistoryItem(triggerId, itemId) {
  return axios
    .post('/insurance/lead/trigger/history/retry', { triggerId, itemId });
}
// See the renewals twin: one list across every leads trigger, scoped server-side.
export function fetchTriggerActivity(payload) {
  return axios
    .post('/insurance/lead/trigger/history/all', payload);
}
export function retryTriggerActivityBulk(payload) {
  return axios
    .post('/insurance/lead/trigger/history/retry-bulk', payload);
}
const actions = {
  fetchLeadTotalCount({ commit }) {
    return axios.post('/insurance/lead/list', { page: 0, limit: 1 })
      .then((response) => {
        commit('setLeadTotalCount', response.data.totalCount);
      })
      .catch((error) => {
        console.error('Error fetching lead total count:', error);
        // Unknown is not zero — see setLeadTotalCountFailed.
        commit('setLeadTotalCountFailed', true);
      });
  },
  fetchLeads({ commit, state, dispatch }) {
    commit('setLeadsListLoading', true);
    return getLeads(state.requestFilters, {}, state.contextFilters)
      .then((response) => {
        commit('setLeads', response.data);
        commit('updateFilteredLeads');
        commit('setLeadsListLoading', false);
        // This response could not refresh the account-wide total (see setLeads), so ask for it
        // separately — otherwise it would go stale for as long as filters stay applied.
        if (hasActiveRequestFilters(state.requestFilters, state.contextFilters)) {
          dispatch('fetchLeadTotalCount');
        }
      })
      .catch((error) => {
        console.error('Error fetching leads:', error);
        commit('setLeadsListLoading', false);
      });
  },

  updateLeads({ commit, dispatch }, leads) {
    // Returned, so callers awaiting this actually wait for the save. It used to return
    // undefined, which resolved immediately and let success toasts fire before the server
    // had done anything — and hid failures entirely.
    return axios
      .post('/insurance/lead/save-bulk', {
        itemsSave: leads,
      })
      .then((response) => {
        dispatch('fetchLeads');
      });
  },

  deleteLeads({ commit, dispatch }, leadIds) {
    const itemsSave = leadIds.map((leadId) => ({
      leadId,
      leadDelete: true,
    }));
    return axios
      .post('/insurance/lead/save-bulk', {
        itemsSave,
      })
      .then((response) => {
        dispatch('fetchLeads');
        commit('removeLeads', leadIds);
        commit('updateFilteredLeads');
      });
  },

  fetchLeadDetails({ commit, dispatch }, leadId) {
    commit('setDetailsLoading', true);
    dispatch('fetchLeadActions', leadId);
    axios
      .post('/insurance/lead/detail', { leadId })
      .then((response) => {
        commit('setLeadDetails', response.data);
        commit('setDetailsLoading', false);
      })
      .catch((error) => {
        console.error('Error fetching lead details:', error);
        commit('setDetailsLoading', false);
      });
  },
  fetchLeadActions({ commit }, leadId) {
    commit('setActionsLoading', true);
    axios
      .post('/insurance/lead/audit-log/list', {
        leadId,
        page: 0,
        limit: 1000,
      })
      .then((response) => {
        commit('setLeadActions', response.data);
        commit('setActionsLoading', false);
      })
      .catch((error) => {
        console.error('Error fetching lead details:', error);
        commit('setActionsLoading', false);
      });
  },
  saveLead({
    commit, dispatch, rootGetters, rootState
  }, body) {
    return axios
      .post('/insurance/lead/save', {
        ...body,
      })
      .then((response) => {
        dispatch('fetchLeads');
        if (body.leadId) {
          dispatch('fetchLeadDetails', body.leadId);
        } else if (rootState.Onboarding.isOnboardingSimulated && !rootState.Onboarding.simulatedLeadCreated) {
          commit('SET_SIMULATED_LEAD_CREATED', true);
          dispatch('checkAndCompleteSimulation');
        }
        return response;
      });
  },
  // Optimistic single-field save for inline card edits (deal premium, payment/commission
  // status). Patches the lead locally so the board updates in place — no full fetchLeads
  // refetch/flicker — then persists. `field` is the Save-suffixed request key, `prop` the
  // matching local lead property.
  saveLeadField({ commit }, {
    leadId, field, value, prop,
  }) {
    commit('updateLeadFields', { leadId, changes: { [prop]: value } });
    return axios
      .post('/insurance/lead/save', { leadId, [field]: value })
      .catch((error) => {
        console.error('Error saving lead field:', error);
      });
  },
  saveLeadAgent({ commit }, { leadId, agentId, agentName }) {
    const agent = agentId ? { id: agentId, name: agentName } : null;
    commit('updateLeadAgent', { leadId, agent });
    const body = agentId
      ? { leadId, agentIdSave: agentId }
      : { leadId, agentIdDelete: true };
    return axios.post('/insurance/lead/save', body);
  },
  exportLeads({ commit, dispatch }, leads) {
    axios
      .post('/insurance/lead/download', {
        leads,
      })
      .then((resp) => {
        const fileName = 'Leads';

        const link = document.createElement('a');
        link.href = URL.createObjectURL(
          new Blob([resp.data], {
            type: 'text/csv',
          })
        );
        link.download = fileName;
        link.click();

        URL.revokeObjectURL(link.href);
      })
      .catch((error) => {
        console.error('Error fetching lead details:', error);
      });
  },
  addLeadFile({ commit, dispatch }, body) {
    axios
      .post('/insurance/lead/save', {
        ...body,
      })
      .then((response) => {
        dispatch('fetchLeadDetails', body.leadId);
      })
      .catch((error) => {
        console.error('Error add file:', error);
      });
  },

  deleteLeadFile({ commit, dispatch }, body) {
    axios
      .post('/insurance/lead/save', {
        ...body,
      })
      .then((response) => {
        dispatch('fetchLeadDetails', body.leadId);
      })
      .catch((error) => {
        console.error('Error delete file:', error);
      });
  },
  updateLeadsGroups({ dispatch }, leadGroups) {
    axios
      .post('/insurance/lead/customstage/save-bulk', {
        leadGroups,
      })
      .then((response) => {
        dispatch('fetchLeadsGroups');
      })
      .catch((error) => {
        console.error('Error updating leads:', error);
      });
  },
  updateLeadsFilters({ commit }, filters) {
    commit('setFilters', filters);
    commit('updateFilteredLeads');
  },
  updateLeadsRequestFilters({ commit, dispatch }, filters) {
    commit('setRequestFilters', filters);
    dispatch('fetchLeads');
  },
  clearFilters({ commit }) {
    commit('clearAllFilters');
    commit('updateFilteredLeads');
  },
  // Full reset of every filter bucket (client, request AND locked context) back to defaults.
  // Used by the dashboard deep-link so stale filters from a previous visit can't interfere, and by
  // the context banner's "Clear" button.
  resetLeadsFilters({ commit }) {
    commit('resetAllFilters');
    commit('updateFilteredLeads');
  },
  // Land on the leads list from a sales-breakdown click: wipe any prior filters, then apply the
  // locked WIN+period context plus the clicked insurer (client filter) / product (request filter),
  // and fetch once so the list matches the dashboard exactly.
  applyLeadsDashboardContext({ commit, dispatch }, { contextFilters, filters, requestFilters }) {
    commit('resetAllFilters');
    if (contextFilters) commit('setContextFilters', contextFilters);
    if (filters) commit('setFilters', filters);
    if (requestFilters) commit('setRequestFilters', requestFilters);
    return dispatch('fetchLeads');
  },
  addLeadsGroup({ commit, state }, leadGroup) {
    return axios
      .post('/insurance/lead/customstage/save', leadGroup)
      .then((response) => {
        commit('addLeadGroup', {
          ...leadGroup,
          ...response.data,
        });
      })
      .catch((error) => {
        console.error('Error fetching leads groups:', error);
      });
  },
  updateLeadsGroup({ dispatch, state }, data) {
    return axios
      .post('/insurance/lead/customstage/save', { ...data })
      .then((response) => {
        dispatch('fetchLeadsGroups');
        // commit('deleteLeadsGroup', customStageId);
        // commit('updateFilteredLeads');
      })
      .catch((error) => {
        console.error('Error fetching leads groups:', error);
      });
  },
  // The backend relocates the stage's leads itself, so the destination travels with the
  // delete: moveToCustomStageId for another custom stage, moveToStage for a built-in one.
  // Sending neither keeps the old behaviour of moving everything to Incoming.
  deleteLeadsGroup(_, { customStageId, moveToCustomStageId = null, moveToStage = null }) {
    return axios.post('/insurance/lead/customstage/delete', {
      customStageId,
      moveToCustomStageId,
      moveToStage,
    });
  },
  fetchLeadsGroups({ commit, state }) {
    return axios
      .post('/insurance/lead/customstage/list', {})
      .then((response) => {
        commit('setLeadsGroups', [
          {
            stage: LEADS_TYPES.INCOMING,
            name: LEADS_TYPES.INCOMING,
            order: 0.1,
            disableEditing: true,
          },
          ...response.data.items.map((item) => ({
            ...item,
            stage: item.id,
            customStageId: item.id,
          })),
          {
            stage: LEADS_TYPES.WIN,
            name: LEADS_TYPES.WIN,
            order: 10000,
            disableEditing: true,
          },
          {
            stage: LEADS_TYPES.LOST,
            name: LEADS_TYPES.LOST,
            order: 10001,
            disableEditing: true,
          },
        ])?.map((item, order) => ({
          ...item,
          order: order + 1,
        }));
      })
      .catch((error) => {
        console.error('Error fetching leads groups:', error);
      });
  },
  // --- Custom fields (REN-1073) ---------------------------------------------
  fetchCustomFields({ commit }) {
    return axios
      .post('/insurance/customfield/list', { entityType: 'LEAD' })
      .then((response) => {
        commit('setCustomFields', response.data.items || []);
        return response.data.items || [];
      })
      .catch((error) => {
        console.error('Error fetching custom fields:', error);
      });
  },
  saveCustomField({ dispatch }, body) {
    return axios
      .post('/insurance/customfield/save', body)
      .then((response) => {
        dispatch('fetchCustomFields');
        return response.data;
      });
  },
  // Field + impact counts for the destructive delete-confirm modal.
  fetchCustomFieldDetail(_, fieldId) {
    return axios
      .post('/insurance/customfield/detail', { fieldId })
      .then((response) => response.data);
  },
  deleteCustomField({ dispatch }, fieldId) {
    return axios
      .post('/insurance/customfield/delete', { fieldId })
      .then((response) => {
        dispatch('fetchCustomFields');
        return response.data;
      });
  },
  // --- Acquisition channels (REN-1073) --------------------------------------
  fetchAcquisitionChannels({ commit }) {
    return axios
      .post('/insurance/lead/acquisitionchannel/list', {})
      .then((response) => {
        commit('setAcquisitionChannels', response.data.items || []);
        return response.data.items || [];
      })
      .catch((error) => {
        console.error('Error fetching acquisition channels:', error);
      });
  },
  saveAcquisitionChannel({ dispatch }, body) {
    return axios
      .post('/insurance/lead/acquisitionchannel/save', body)
      .then((response) => {
        dispatch('fetchAcquisitionChannels');
        return response.data;
      });
  },
  deleteAcquisitionChannel({ dispatch }, channelId) {
    return axios
      .post('/insurance/lead/acquisitionchannel/delete', { channelId })
      .then((response) => {
        dispatch('fetchAcquisitionChannels');
        return response.data;
      });
  },
  // --- Custom insurance types (Sales hub) ------------------------------------
  fetchCustomInsuranceTypes({ commit }) {
    return axios
      .post('/insurance/lead/custominsurancetype/list', { includeDeleted: true })
      .then((response) => {
        commit('setCustomInsuranceTypes', response.data.items || []);
        return response.data.items || [];
      })
      .catch((error) => {
        console.error('Error fetching custom insurance types:', error);
      });
  },
  // Resolves after the list is refreshed, so the caller's next `order` sees the type just added.
  saveCustomInsuranceType({ dispatch }, body) {
    return axios
      .post('/insurance/lead/custominsurancetype/save', body)
      .then((response) => dispatch('fetchCustomInsuranceTypes').then(() => response.data));
  },
  // Soft delete: leads keep the type (and the dashboard its sales); it only leaves the pick list.
  deleteCustomInsuranceType({ dispatch }, customInsuranceTypeId) {
    return axios
      .post('/insurance/lead/custominsurancetype/delete', { customInsuranceTypeId })
      .then((response) => {
        dispatch('fetchCustomInsuranceTypes');
        return response.data;
      });
  },
  fetchLeadTriggers({commit, state}) {
    commit('setLeadsListLoading', true);
    axios.post('/insurance/lead/trigger/list', {}).then((response) => {
      commit('setLeadTriggers', response.data);
    }).catch((error) => {
      console.error('Error fetching leads:', error);
    });
  },
  createLeadTrigger({commit, dispatch}, trigger) {
    commit('setLeadsListLoading', true);
    axios.post('/insurance/lead/trigger/save', trigger).then((response) => {
      dispatch('fetchLeads', response.data);
      dispatch('fetchLeadTriggers', response.data);
    }).catch((error) => {
      console.error('Error fetching leads:', error);
      throw new Error(error);
    });
  },
  deleteLeadTrigger({commit, dispatch}, triggerId) {
    commit('setLeadsListLoading', true);
    return axios
      .post('/insurance/lead/trigger/delete', { triggerId })
      .then((response) => {
        dispatch('fetchLeads', response.data);
        dispatch('fetchLeadTriggers', response.data);
      })
      .catch((error) => {
        throw new Error(error);
      });
  },
};

const mutations = {
  setLeads(state, leads) {
    state.leads.items = leads.items;
    state.leads.listCount = leads.totalCount;
    // A filtered response describes the current query, not the account. leadTotalCount drives
    // onboarding, demo data and the empty-state placeholder app-wide, so filtering the board
    // down to nothing must not read as "this account has no leads".
    if (!hasActiveRequestFilters(state.requestFilters, state.contextFilters)) {
      state.leads.totalCount = leads.totalCount;
    }
    // The list response also carries the custom-field schema (returned once); keep it
    // in sync so kanban cards can render label:value without a separate request.
    if (Array.isArray(leads.customFields)) {
      state.customFields = leads.customFields;
    }
  },
  setCustomFields(state, customFields) {
    state.customFields = customFields;
  },
  setAcquisitionChannels(state, channels) {
    state.acquisitionChannels = channels;
  },
  setCustomInsuranceTypes(state, types) {
    state.customInsuranceTypes = types;
  },
  setLeadTotalCount(state, totalCount) {
    state.leads.totalCount = totalCount;
    state.leadTotalCountFailed = false;
  },
  // A failed count request must not read as "no leads yet" — that is what turns the
  // first-run onboarding overlay on and blocks the page with pointer-events: none.
  setLeadTotalCountFailed(state, failed) {
    state.leadTotalCountFailed = failed;
  },

  setLeadsListLoading(state, loading) {
    state.leads.loading = loading;
  },

  setLeadDetails(state, details) {
    state.leadDetails.data = details;
  },
  setLeadActions(state, actions) {
    state.leadDetails.actions = actions;
  },

  setDetailsLoading(state, loading) {
    state.leadDetails.loading = loading;
  },
  setActionsLoading(state, loading) {
    state.leadDetails.actions.loading = loading;
  },

  setUpdatedLeads(state, updatedLeads) {
    state.leads.items = state.leads.items.map((lead) => {
      const updatedLead = updatedLeads.find((val) => val.leadId === lead.leadId);
      if (updatedLead) {
        return {
          ...lead,
          ...updatedLead,
          stage: updatedLead.stageSave,
        };
      }
      return lead;
    });
  },

  removeLeads(state, leadIds) {
    state.leads.items = state.leads.items.filter((lead) => !leadIds.includes(lead.leadId));
  },

  updateLeadFields(state, { leadId, changes }) {
    state.leads.items = state.leads.items.map((lead) => (
      lead.leadId === leadId ? { ...lead, ...changes } : lead
    ));
    state.filteredLeadsList = filterLeads(state.leads.items, state.filters);
  },

  updateLeadAgent(state, { leadId, agent }) {
    state.leads.items = state.leads.items.map((lead) => {
      if (lead.leadId !== leadId) return lead;
      return { ...lead, agent };
    });
    state.filteredLeadsList = filterLeads(state.leads.items, state.filters);
  },

  setFilters(state, filters) {
    state.filters = { ...state.filters, ...filters };
  },

  clearAllFilters(state) {
    state.filters = { ...defaultLeadsFilters };
  },
  resetAllFilters(state) {
    state.filters = { ...defaultLeadsFilters };
    state.requestFilters = { ...defaultRequestFilters };
    state.contextFilters = { ...defaultContextFilters };
  },
  updateFilteredLeads() {
    state.filteredLeadsList = filterLeads(state.leads.items, state.filters);
  },
  setRequestFilters(state, requestFilters) {
    state.requestFilters = { ...state.requestFilters, ...requestFilters };
  },
  setContextFilters(state, contextFilters) {
    state.contextFilters = { ...state.contextFilters, ...contextFilters };
  },
  setLeadsGroups(state, groups) {
    state.leadsGroups = groups;
    state.filteredLeadsList = filterLeads(state.leads.items, state.filters);
  },
  addLeadGroup(state, group) {
    state.leadsGroups = [...state.leadsGroups, group];
    state.filteredLeadsList = filterLeads(state.leads.items, state.filters);
  },
  deleteLeadsGroup(state, group) {
    state.leadsGroups = [...state.leadsGroups.filter((val) => val.customStageId !== group)];
    state.filteredLeadsList = filterLeads(state.leads.items, state.filters);
  },
  setLeadTriggers(state, triggers) {
    state.leadsTriggers = cloneDeep([...triggers.items]);
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
